import useSelectionStore from '@store/useSelectionStore';
import useGameStore from '@store/useGameStore';
import { ClickableType } from '@utils/enums';
import { CONFIG } from '@utils/constants';
import StatDisplay from './StatDisplay';
import UnitIcon from '@gameobjects/UnitIcon';
import HoverClickPopup from '@components/ui/general/HoverClickPopup';
import EffectBar from '@components/ui/game/state-indicator/EffectBar';
import { Swords, Shield, Footprints, Brain, Eye, Heart, Sparkles, Activity } from 'lucide-react';
import './UnitInfoPanel.css';

export default function UnitInfoPanel() {
  const { currentClick } = useSelectionStore();
  // Subscribe to snapshot changes to update during cinematics
  const currentSnapshotIndex = useGameStore(state => state.currentSnapshotIndex);

  // Only show if a unit is selected
  if (!currentClick || currentClick.type !== ClickableType.UNIT) {
    return null;
  }

  // Get fresh unit data from game store
  const unit = useGameStore.getState().getUnitOrAvailableUnitById(currentClick.data?.id);

  if (!unit) {
    console.warn('[UnitInfoPanel] Unit not found:', currentClick.data?.id);
    return null;
  }

  // Validate unit has required data - FAIL LOUDLY
  if (!unit.unit_template) {
    throw new Error(`[UnitInfoPanel] Unit ${unit.id} missing unit_template`);
  }

  const template = unit.unit_template;

  // Validate required template fields
  if (template.max_hp === undefined || template.max_hp === null) {
    throw new Error(`[UnitInfoPanel] Unit template missing max_hp for unit ${unit.id}`);
  }
  if (template.max_ap === undefined || template.max_ap === null) {
    throw new Error(`[UnitInfoPanel] Unit template missing max_ap for unit ${unit.id}`);
  }

  // Calculate HP percentage
  if (unit.hp === undefined || unit.hp === null) {
    throw new Error(`[UnitInfoPanel] Unit ${unit.id} missing hp value`);
  }

  const currentHp = Math.floor(unit.hp);
  const maxHp = Math.floor(template.max_hp);
  const hpPercentage = (currentHp / maxHp) * 100;

  // Get team color class
  if (!unit.teamClass) {
    throw new Error(`[UnitInfoPanel] Unit ${unit.id} missing teamClass`);
  }
  const teamClass = unit.teamClass;

  // Build HP popup content
  const hpPopupContent = (
    <div className="hp-popup">
      <div className="hp-popup-header">
        <Activity size={16} />
        <span>Health Status</span>
      </div>

      <div className="hp-popup-content">
        <div className="hp-stat-row">
          <span>Current HP</span>
          <span className="hp-value">{currentHp}</span>
        </div>
        <div className="hp-stat-row">
          <span>Maximum HP</span>
          <span className="hp-value">{maxHp}</span>
        </div>
        {currentHp < maxHp && (
          <>
            <div className="hp-separator" />
            <div className="hp-stat-row damage">
              <span>Damage Taken</span>
              <span className="hp-value">-{maxHp - currentHp}</span>
            </div>
            <div className="hp-stat-row">
              <span>Health %</span>
              <span className="hp-value">{Math.floor(hpPercentage)}%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Determine HP text class based on percentage
  const getHpTextClass = () => {
    if (hpPercentage > 50) return 'hp-text-healthy';
    if (hpPercentage > 25) return 'hp-text-warning';
    return 'hp-text-critical';
  };

  // Build HP bar element
  const hpBarElement = (
    <div className="hp-bar">
      <div
        className="hp-fill"
        style={{
          width: `${hpPercentage}%`,
          backgroundColor: hpPercentage > 50 ? '#4ade80' : hpPercentage > 25 ? '#fbbf24' : '#f87171'
        }}
      />
      <span className={`hp-text ${getHpTextClass()}`}>
        {currentHp}/{maxHp} HP
      </span>
    </div>
  );

  // Check if unit has active effects
  const hasEffects = unit.active_effects && unit.active_effects.length > 0;

  return (
    <div className="unit-info-card">
      <div className="unit-info-header">
        <UnitIcon unitId={unit.id} size={60} />
        <h3 className={`unit-title team-${teamClass}`}>{unit.name}</h3>
        {CONFIG.SHOW_COORD && (
          <div className="unit-position">
            <span>({unit.x}, {unit.y})</span>
          </div>
        )}
        {hasEffects && <EffectBar unitId={unit.id} />}
      </div>

      {/* HP Bar with popup */}
      <HoverClickPopup
        trigger={hpBarElement}
        className="hp-popup-container"
      >
        {hpPopupContent}
      </HoverClickPopup>

      {/* Stats Grid using StatDisplay */}
      <div className="stats-grid">
        <StatDisplay
          unit={unit}
          statName="strength"
          icon={<Swords size={14} />}
          tooltip="Physical damage dealt in combat"
        />

        <StatDisplay
          unit={unit}
          statName="armor"
          icon={<Shield size={14} />}
          tooltip="Physical damage reduction"
        />

        <StatDisplay
          unit={unit}
          statName="movement"
          icon={<Footprints size={14} />}
          tooltip="Movement range per turn"
        />

        <StatDisplay
          unit={unit}
          statName="agility"
          icon={<Brain size={14} />}
          tooltip="Turn order and dodge chance"
        />

        <StatDisplay
          unit={unit}
          statName="vision"
          icon={<Eye size={14} />}
          tooltip="Sight range for detecting enemies"
        />

        <StatDisplay
          unit={unit}
          statName="will"
          icon={<Heart size={14} />}
          tooltip="Mental resistance and morale"
        />

        <StatDisplay
          unit={unit}
          statName="psi"
          icon={<Sparkles size={14} />}
          tooltip="Psychic power and abilities"
        />
      </div>
    </div>
  );
}